import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export function useObjectObservations({
    objectId,
    electricalProjectVersionId,
    objectKind,
}: {
    objectId: string;
    electricalProjectVersionId: string;
    objectKind?: components['schemas']['ElectricalProjectObservationObjectKind'];
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!objectId) {
        throw new Error('objectId is required');
    }

    const observations = useSuspenseQuery(elektraAppQueryOptions.project.observationsListOptions({
        electricalProjectVersionId,
    }));

    const filteredObservations = observations.data?.Observations?.filter(observation =>
        (observation?.ElectricalProjectCheckListItemId === objectId
            || observation?.ElectricalProjectDistributionBoardDeviceId === objectId
            || observation?.ElectricalProjectDistributionBoardFileId === objectId
            || observation?.ElectricalProjectDistributionBoardId === objectId
            || observation?.ElectricalProjectFinalCircuitId === objectId
            || observation?.ElectricalProjectObservationId === objectId
            || observation?.ElectricalProjectRisoMeasurementId === objectId
            || observation?.ElectricalProjectPVInverterId === objectId)
        && observation.IsDeleted !== true && observation.ObjectKind === objectKind,
    ) || [];

    return {
        data: {
            observations: filteredObservations as components['schemas']['ListElectricalProjectObservationQueryResultObservation'][],
            count: filteredObservations.length,
        },
        isLoading: observations.isLoading,
        isError: observations.isError,
        error: observations.error,
    };
}

export function useObservation({
    electricalProjectObservationId,
    electricalProjectVersionId,
}: {
    electricalProjectObservationId: string;
    electricalProjectVersionId: string;
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!electricalProjectObservationId) {
        throw new Error('observationId is required');
    }

    const observations = useSuspenseQuery(elektraAppQueryOptions.project.observationsListOptions({
        electricalProjectVersionId,
    }));

    const observation = useMemo(() => observations.data?.Observations?.find(
        observation => observation.ElectricalProjectObservationId === electricalProjectObservationId,
    ) || null, [observations.data, electricalProjectObservationId]);

    return {
        data: {
            observation: observation as components['schemas']['ListElectricalProjectObservationQueryResultObservation'] | null,
        },
        isLoading: observations.isLoading,
        isError: observations.isError,
        error: observations.error,
    };
}
